import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import axios from "axios";
import SearchContent from "../../Views/SearchContent/SearchContent";
import NewsArticleSearch from "../../Views/NewsArticle/NewsArticleSearch";
import AnalyzedTweetSearch from "../../Views/AnalyzedTweet/AnalyzedTweetSearch";
import TweetsList from "../../Actions/TweetsList";
import { newsTypes } from "./search.types";
import "./Search.scss";
import { useNavigate } from "react-router-dom";
import SubscribeListingModal from "../../Views/Common/SubscribeListingModal/SubscribeListingModal";
import Navbar from "../LandingV2/Navbar";
import ReactMarkdown from "react-markdown";
import { InfoCircleOutlined } from "@ant-design/icons";

const searchHighlightEnum = {
  AiSummary: 0,
  SearchContent: 1,
  NewsArticle: 2,
};

const searchInfoBoxText = {
  [searchHighlightEnum.AiSummary]: "Provides an AI-generated summary of the topic based on search results.",
  [searchHighlightEnum.SearchContent]: "Lists related news articles or tweets matching your search query.",
  [searchHighlightEnum.NewsArticle]: "Displays the full content and details of the selected news article or tweet.",
};
/**
 * This React component represents the search functionality within the application.
 *
 * Users can enter search queries to retrieve news and tweet results, and the component
 * displays search results, filters, and additional details for each result.
 *
 * @returns {JSX.Element} The JSX representation of the News component.
 */
const News = () => {
  const [apiResponse, setApiResponse] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [newsData, setNewsData] = useState<any | {}>({} as any);
  // const [wordCloud, setWordCloud] = useState<any | {}>({} as any);
  const [singleNews, setSingleNews] = useState<any | {}>({} as any);
  const [allNews, setAllNews] = useState<newsTypes[] | []>([]);
  const [isLoaderVisible, setIsLoaderVisible] = useState<boolean>(false);
  const [wordCloudData, setWordCloudData] = useState<any>([]);
  const [filterNews, setFilterNews] = useState<newsTypes[] | []>([]);
  const [checkSearchResults, setCheckSearchResults] = useState<any | []>([]);
  const [searchError, setSearchError] = useState<string>("");

  // Page Tour State
  const [showInfoBox, setShowInfoBox] = useState<boolean>(false);
  const [highlightBox, sethighlightBox] = useState<number>(searchHighlightEnum.AiSummary);
  const infoBoxRefList = useRef<any[]>([]);

  const setInfoBoxRef = (index: number) => (ref: any) => {
    infoBoxRefList.current[index] = ref;
  };

  const handleInfoBox = (boxType: number) => {
    if (showInfoBox && highlightBox === boxType) {
      setShowInfoBox(false);
    } else {
      sethighlightBox(boxType);
      setShowInfoBox(true);
    }
  };

  // ── Outer resize (AI Summary ↔ News Results) ──
  const [leftPct, setLeftPct] = useState<number>(26);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const onDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.min(Math.max(pct, 15), 55));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  // ── Inner resize (Contents ↔ Summary) ──
  const [innerLeftPct, setInnerLeftPct] = useState<number>(35);
  const innerIsDragging = useRef(false);
  const innerContainerRef = useRef<HTMLDivElement>(null);

  const onInnerDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    innerIsDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (ev: MouseEvent) => {
      if (!innerIsDragging.current || !innerContainerRef.current) return;
      const rect = innerContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setInnerLeftPct(Math.min(Math.max(pct, 20), 65));
    };

    const onMouseUp = () => {
      innerIsDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const listIndex = useSelector((state: any) => state.NewsListData);
  const newsByWord = useSelector((state: any) => state.IdentifyNews);
  const dispatch = useDispatch();
  const initial = 5;

  const [bar] = useState(initial);

  const [showSubScribe, setShowSubScribe] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(TweetsList(null));
  }, []);

  // Setting filterType to filter out news or tweets
  useEffect(() => {
    if (filterType === "news") {
      let only_news = newsData.filter((word: { key: string }) => {
        return word.key === "news";
      });
      only_news[0].active = true;
      setAllNews(() => {
        return [...only_news];
      });
      setSingleNews(only_news[0]);
    } else if (filterType === "tweets") {
      let only_tweets = newsData.filter((word: { key: string }) => {
        return word.key === "tweets";
      });
      only_tweets[0].active = true;
      setAllNews(() => {
        return [...only_tweets];
      });
      setSingleNews(only_tweets[0]);
    } else if (filterType === "all") {
      setAllNews(() => {
        return [...newsData];
      });
      newsData[0].active = true;
      setSingleNews(newsData[0]);
    }
  }, [filterType]);

  const getAllNews = async () => {
    try {
      //reset prev
      setWordCloudData([]);

      setIsLoaderVisible(true);

      let response_from_api = await axios.post(
        `${process.env.REACT_APP_SEARCH_URL}/`,
        {
          prompt: searchText,
          get_window: 7,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      // console.log("response_from_api", response_from_api.data);
      setApiResponse(response_from_api.data.response);

      // let responsefromdummy = testdata;
      let search_result: any[] = [];
      let news_response: any[] = [];

      const metaData = response_from_api.data?.meta_data;
      if (!metaData || (Array.isArray(metaData) && metaData.length === 0)) {
        setSearchError("No results found or backend returned an invalid response.");
      }

      // The search API returns different meta_data shapes depending on the query:
      //   • News:         { long_description, news_for, news_ner_flag }
      //   • Fundamentals: { ticker_name, date, mutual_fund_holdings }
      // The older field names the UI was built for (News_Summary / News_Title /
      // News_date / News_Source …) are no longer sent, so normalize the current
      // fields onto those names here. Everything downstream (SearchContent list,
      // the detail pane, sorting, and the empty-state guard) reads these names.
      const metaList: any[] = Array.isArray(metaData)
        ? metaData
        : metaData && typeof metaData === "object"
        ? Object.values(metaData)
        : [];

      const parseMetaDate = (raw: any): string | undefined => {
        if (!raw) return undefined;
        const m = moment(raw, ["YYYY-MMMM-DD", "YYYY-MM-DD", moment.ISO_8601], true);
        return m.isValid() ? m.toISOString() : undefined;
      };

      const normalizeMeta = (meta: any, index: number) => {
        const id = "news_" + index;
        // Fundamentals / mutual-fund holdings shape
        if (meta?.mutual_fund_holdings) {
          const ticker = meta.ticker_name || "Company";
          return {
            key: "news",
            News_ID: id,
            ID: id,
            News_Title: `${ticker} — Mutual Fund Holdings`,
            News_Summary: meta.mutual_fund_holdings,
            Sentiment_Analyzed_news: meta.mutual_fund_holdings,
            News_Source: ticker,
            News_Sentiment: "neutral",
            News_date: parseMetaDate(meta.date),
          };
        }
        // News shape
        if (meta?.long_description) {
          return {
            key: "news",
            News_ID: id,
            ID: id,
            News_Title: meta.long_description,
            News_Summary: meta.long_description,
            Sentiment_Analyzed_news: meta.long_description,
            News_Source: meta.news_for || "News",
            News_Sentiment: "neutral",
            News_date: parseMetaDate(meta.date || meta.News_date),
          };
        }
        // Unknown shape — surface whatever text we can find so nothing is silently dropped.
        const firstText =
          (Object.values(meta || {}).find((v) => typeof v === "string") as string) || "";
        return {
          key: "news",
          News_ID: id,
          ID: id,
          News_Title: meta?.ticker_name || meta?.news_for || "Result",
          News_Summary: firstText,
          Sentiment_Analyzed_news: firstText,
          News_Source: meta?.ticker_name || meta?.news_for || "Result",
          News_Sentiment: "neutral",
          News_date: parseMetaDate(meta?.date),
        };
      };

      metaList.forEach((meta: any, index: number) => {
        const normalized = normalizeMeta(meta, index);
        news_response.push(normalized);
        search_result.push({
          text: normalized.News_Summary,
          ids: [normalized.News_ID],
          key: "news",
        });
      });

      setCheckSearchResults(search_result);
      setWordCloudData(search_result);
      setNewsData(news_response);
      setAllNews(news_response);

      news_response.forEach((element: any) => {
        element.active = false;
        element.filterDate = moment(element.News_date).format("YYYY-MM-DD");
      });

      let sortedList: any = news_response.sort(function (a: any, b: any) {
        let date1: any = new Date(a.News_date);
        let date2: any = new Date(b.News_date);
        return date2 - date1;
      });

      if (listIndex !== null) {
        let findNewsIndex = sortedList.findIndex(
          (news: any) => news.News_ID === listIndex
        );

        if (findNewsIndex === -1) {
          findNewsIndex = 0;
        }

        sortedList[findNewsIndex].active = true;
        setSingleNews(sortedList[findNewsIndex]);
      } else {
        if (sortedList.length) {
          sortedList[0].active = true;
          setSingleNews(sortedList[0]);
        }
      }

      setFilterNews(sortedList);
    } catch (err) {
      console.error(err);
      setWordCloudData([]);
    } finally {
      setIsLoaderVisible(false);
    }
  };

  useEffect(() => {
    var result: any = [];
    for (var i = 0; i < Math.floor(bar); i++) {
      var d = new Date();
      d.setDate(d.getDate() - i);
      result.push(moment(d).format("YYYY-MM-DD"));
    }

    if (bar > 0) {
      const newsFilter = filterNews.filter((news) => {
        return result.includes(news.filterDate);
      });

      if (newsFilter.length > 0) {
        for (let i = 0; i < newsFilter.length; i++) {
          newsFilter[i].active = false;
        }
        newsFilter[0].active = true;
        setSingleNews(newsFilter[0]);
      } else {
        setSingleNews({});
      }
      setAllNews(newsFilter);
    }
  }, [bar]);

  useEffect(() => {
    if (newsByWord.length > 0) {
      const filterNewsByWords = filterNews.filter((news) =>
        newsByWord.includes(news.ID)
      );

      if (filterNewsByWords.length > 0) {
        for (let i = 0; i < filterNews.length; i++) {
          filterNews[i].active = false;
        }

        filterNewsByWords[0].active = true;
        setAllNews(() => {
          return [...filterNewsByWords];
        });
        setSingleNews(filterNewsByWords[0]);
      } else {
        if (bar > 0) {
          if (filterNews.length > 0) {
            for (let i = 0; i < filterNews.length; i++) {
              filterNews[i].active = false;
            }
            filterNews[0].active = true;
            setSingleNews(filterNews[0]);
          } else {
            setSingleNews({});
          }
          setAllNews(filterNews);
        }
      }
    }
  }, [newsByWord]);

  const handleSearchSubmit = (event: any) => {
    event.preventDefault();

    if (!searchText.trim()) {
      setSearchError("Please enter a prompt before searching.");
      return;
    }

    setSearchError("");
    const subscribed = true; //check if user is premium

    if (subscribed) {
      getAllNews();
    } else {
      //if not premium --> show subscribe popover
      setShowSubScribe(true);
    }
  };

  useEffect(() => {
    if (showInfoBox && infoBoxRefList.current) {
      const cardRef = infoBoxRefList.current[highlightBox];
      if (!cardRef) {
        console.log("Could not get bounding ref");
        return;
      }
      
      const cardRect = cardRef.getBoundingClientRect();

      // Scroll completely to the bottom of the page when the tour starts
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      const infoBox = document.getElementById("infoBox");
      if (infoBox) {
        let infoBoxWidth = infoBox.offsetWidth;
        const viewportWidth = window.innerWidth;
        let leftPosition = cardRect.right + window.scrollX + 20;
        let topPosition = cardRect.top + window.scrollY;

        // Give it sensible default dimensions when rendering on desktop
        infoBox.style.width = "250px";
        infoBox.style.fontSize = "14px";
        infoBox.style.height = "auto";
        // Ensure no leftover mobile styling corrupts the desktop layout
        infoBoxWidth = 250;

        // Dynamically figure out if the right side is too close to the edge of the screen
        // If it crosses the middle of the viewport (or offscreen), place to the left instead
        if (cardRect.right + infoBoxWidth + 50 > viewportWidth) {
          leftPosition = cardRect.left + window.scrollX - infoBoxWidth - 30;
        }

        // Prevent the infobox from rendering off completely on the left side of the screen!
        leftPosition = Math.max(15, leftPosition);

        if (viewportWidth < 768) {
          // Mobile stack view - place directly under the card
          leftPosition = Math.max(20, (viewportWidth - infoBoxWidth) / 2); // Center horizontally roughly
          infoBox.style.width = "180px";
          infoBox.style.fontSize = "12px";
          topPosition = cardRect.bottom + window.scrollY + 5;
        }
        infoBox.style.top = `${topPosition}px`;
        infoBox.style.left = `${leftPosition}px`;
        infoBox.style.zIndex = "1040";
      }
    }
  }, [showInfoBox, highlightBox]);

  useEffect(() => {
    if (showInfoBox) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.paddingRight = "";
      };
    }
  }, [showInfoBox]);

  const hasResults = wordCloudData.length !== 0 && newsData.length !== 0;

  return (
    <>
      <Navbar />
      {showInfoBox && (
        <>
          <div
            id="infoBox"
            className="info-box"
            style={{
              position: "absolute",
              transition: "top 0.4s ease, left 0.4s ease",
              zIndex: 1040,
              backgroundColor: "#fff",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              borderRadius: "12px",
              borderTop: "6px solid #4285F4",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div style={{ padding: "24px 24px 20px 24px" }}>
              <div className="info-box-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <div className="ib-title" style={{ fontSize: "14px", fontWeight: "700", color: "#4285F4", letterSpacing: "1px" }}>PAGE TOUR</div>
                <span
                  className="close-x"
                  onClick={() => setShowInfoBox(false)}
                  style={{ cursor: "pointer", color: "#64748b" }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </span>
              </div>
              <div className="info-box-content" style={{ fontSize: "15.5px", color: "#475569", lineHeight: "1.6", margin: 0 }}>
                {searchInfoBoxText[highlightBox]}
              </div>
            </div>
            <div className="info-box-bottom" style={{ display: "flex", justifyContent: "center", backgroundColor: "#f8fafc", padding: "16px 24px", borderTop: "1px solid #f1f5f9" }}>
              <div className="pagination-container" style={{ display: "flex", alignItems: "center", gap: "24px" }}>
                <span
                  className="nav-arrow"
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#e8f0fe", color: "#4285F4", transition: "background-color 0.2s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#d2e3fc"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#e8f0fe"}
                  onClick={() =>
                    sethighlightBox((prev) => {
                      const max = Object.keys(searchHighlightEnum).length - 1;
                      return prev === 0 ? max : prev - 1;
                    })
                  }
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </span>
                <span className="page-info" style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", minWidth: "46px", textAlign: "center" }}>
                  {highlightBox + 1} <span style={{ color: "#94a3b8", margin: "0 6px", fontWeight: "500" }}>/</span> {Object.keys(searchHighlightEnum).length}
                </span>
                <span
                  className="nav-arrow"
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#e8f0fe", color: "#4285F4", transition: "background-color 0.2s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#d2e3fc"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#e8f0fe"}
                  onClick={() =>
                    sethighlightBox((prev) => {
                      const max = Object.keys(searchHighlightEnum).length - 1;
                      return prev === max ? 0 : prev + 1;
                    })
                  }
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </span>
              </div>
            </div>
          </div>
          <div className="modal-backdrop" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1030 }}></div>
        </>
      )}
      <SubscribeListingModal
        value={showSubScribe}
        onHideModal={() => {
          setShowSubScribe(false);
        }}
      />

      {/* ── Search Hero ── */}
      <div className="search-hero">
        <div className="search-hero-inner">
          <p className="search-tagline">Search across financial news & insights</p>
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-field-wrap">
              <span className="search-icon-left">
                <i className="fas fa-search" />
              </span>
              <input
                type="text"
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setSearchError(""); }}
                className="search-input"
                placeholder="e.g. RBI rate hike impact on banking stocks…"
              />
              {searchText && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchText("");
                    setSearchError("");
                    setApiResponse(null);
                    setWordCloudData([]);
                    setNewsData({});
                    setAllNews([]);
                    setFilterNews([]);
                    setCheckSearchResults([]);
                    setSingleNews({});
                  }}
                  aria-label="Clear search"
                >
                  &#10005;
                </button>
              )}
              <button type="submit" className="search-btn">
                Search
              </button>
            </div>
          </form>
          {searchError && (
            <p className="search-error">
              <span>&#9888;</span> {searchError}
            </p>
          )}
          <p className="search-disclaimer">
            <span>&#9432;</span> Search results are currently in Beta. Please interpret with caution.
          </p>

          {/* ── Example Prompts ── */}
          {!hasResults && (
            <div className="search-faq">
              <p className="search-faq-label">Try asking:</p>
              <div className="search-faq-chips">
                {[
                  "How has the financial of Asian paints been historically?",
                  "How has the financial of ICICI been historically?",
                ].map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="search-faq-chip"
                    onClick={() => { setSearchText(q); setSearchError(""); }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Results ── */}
      {hasResults ? (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 40px", maxWidth: "1600px", margin: "0 auto" }}>
            <div 
              className="premium-tour-btn" 
              onClick={() => handleInfoBox(searchHighlightEnum.AiSummary)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#4285F4', fontWeight: '500', backgroundColor: '#f1f5f9', padding: '8px 16px', borderRadius: '8px', transition: 'background-color 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            >
              <InfoCircleOutlined style={{ fontSize: "16px" }} />
              <span>What's this?</span>
            </div>
          </div>
          <div className="search-results-container" ref={containerRef}>
          {/* Left: AI Summary */}
          <div 
            className="search-panel search-panel--left tour-wrapper" 
            ref={setInfoBoxRef(searchHighlightEnum.AiSummary)}
            style={showInfoBox && highlightBox === searchHighlightEnum.AiSummary ? { position: 'relative', zIndex: 1050, flex: `0 0 ${leftPct}%` } : { flex: `0 0 ${leftPct}%` }}
          >
            <div className="panel-header">
              <span className="panel-header-icon">✦</span>
              <span>AI Summary</span>
            </div>
            <div className="panel-body ai-summary-body">
              {apiResponse ? (
                <ReactMarkdown>{apiResponse}</ReactMarkdown>
              ) : (
                <p className="no-content-text">No summary available.</p>
              )}
            </div>
          </div>

          {/* Drag handle */}
          <div className="search-resize-handle" onMouseDown={onDividerMouseDown} />

          {/* Right: News Results */}
          <div className="search-panel search-panel--right">
            <div className="panel-header">
              <span className="panel-header-icon">&#128240;</span>
              <span>News Results</span>
            </div>
            <div className="panel-body news-panel-body">
              {checkSearchResults[0]?.text === undefined ? (
                <div className="no-data-box">No metadata to display.</div>
              ) : (
                <div className="metabox-resizable" ref={innerContainerRef}>
                  <div 
                    className="metabox-pane tour-wrapper" 
                    ref={setInfoBoxRef(searchHighlightEnum.SearchContent)}
                    style={showInfoBox && highlightBox === searchHighlightEnum.SearchContent ? { position: 'relative', zIndex: 1050, flex: `0 0 ${innerLeftPct}%` } : { flex: `0 0 ${innerLeftPct}%` }}
                  >
                    <SearchContent
                      setSingleNews={setSingleNews}
                      allNews={allNews}
                      filterType={filterType}
                    />
                  </div>
                  <div className="search-resize-handle" onMouseDown={onInnerDividerMouseDown} />
                  <div 
                    className="metabox-pane tour-wrapper" 
                    ref={setInfoBoxRef(searchHighlightEnum.NewsArticle)}
                    style={showInfoBox && highlightBox === searchHighlightEnum.NewsArticle ? { position: 'relative', zIndex: 1050, flex: 1 } : { flex: 1 }}
                  >
                    {singleNews?.key === "news" ? (
                      <NewsArticleSearch singleNews={singleNews} />
                    ) : (
                      <AnalyzedTweetSearch singleTweet={singleNews} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </>
      ) : (
        !isLoaderVisible && (
          <div className="search-empty-state">
            <div className="empty-icon">🔍</div>
            <p className="empty-title">Start your search</p>
            <p className="empty-subtitle">
              Enter a query above to explore news, insights, and market updates.
            </p>
          </div>
        )
      )}

      {/* ── Loader overlay ── */}
      {isLoaderVisible && (
        <div className="search-loader-overlay">
          <div className="loadingspinner">
            <div id="square1"></div>
            <div id="square2"></div>
            <div id="square3"></div>
            <div id="square4"></div>
            <div id="square5"></div>
          </div>
          <p>Searching…</p>
        </div>
      )}
    </>
  );
};

export default React.memo(News);
